import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Send, Mail, MessageSquare, Eye, ChevronLeft, ChevronRight, CheckCircle2, Clock, XCircle, AlertTriangle } from 'lucide-react';

const MSG_TYPES = ['GENERAL_UPDATE', 'SCHEDULE_CHANGE', 'WELLBEING_UPDATE', 'APPOINTMENT_REMINDER', 'SERVICE_NOTICE', 'OTHER'];
const CHANNELS = ['PORTAL', 'SMS', 'EMAIL'];
const STATUS_ICONS = { SENT: CheckCircle2, DELIVERED: CheckCircle2, READ: Eye, FAILED: XCircle };
const STATUS_COLORS = { SENT: 'text-green-500', DELIVERED: 'text-blue-500', READ: 'text-purple-500', FAILED: 'text-red-500' };

function SendMessageDialog({ open, onClose, onSent }) {
  const [participants, setParticipants] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedParticipant, setSelectedParticipant] = useState('');
  const [selectedContact, setSelectedContact] = useState('');
  const [contactDetail, setContactDetail] = useState(null);
  const [form, setForm] = useState({ channel: 'EMAIL', messageType: 'GENERAL_UPDATE', subject: '', messageBody: '' });
  const [sending, setSending] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    if (open) api.get('/participants?page=1&pageSize=50').then(r => setParticipants(r.data.items || r.data.data || [])).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!selectedParticipant) { setContacts([]); return; }
    api.get(`/engagement/contacts/by-participant/${selectedParticipant}?status=ACTIVE`)
      .then(r => setContacts(r.data.items || []))
      .catch(() => setContacts([]));
  }, [selectedParticipant]);

  useEffect(() => {
    if (!selectedContact) { setContactDetail(null); setEligibility(null); return; }
    const c = contacts.find(ct => ct.id === selectedContact);
    setContactDetail(c || null);
    if (c) {
      const consentMap = {
        GENERAL_UPDATE: 'canReceiveGeneralUpdates', SCHEDULE_CHANGE: 'canReceiveScheduleChanges',
        WELLBEING_UPDATE: 'canReceiveMedicalUpdates', APPOINTMENT_REMINDER: 'canReceiveScheduleChanges',
        SERVICE_NOTICE: 'canReceiveGeneralUpdates', OTHER: 'canReceiveGeneralUpdates',
      };
      const flag = consentMap[form.messageType] || 'canReceiveGeneralUpdates';
      setEligibility({ flag, allowed: !!c[flag], contactName: `${c.firstName} ${c.lastName}` });
    }
  }, [selectedContact, contacts, form.messageType]);

  const handleSend = async () => {
    setSending(true);
    try {
      await api.post('/engagement/messages/send', {
        participantId: selectedParticipant, contactId: selectedContact,
        channel: form.channel, messageType: form.messageType,
        subject: form.subject, messageBody: form.messageBody,
      });
      toast.success('Message sent'); onSent();
    } catch (e) {
      const err = e.response?.data?.error || e.response?.data?.detail;
      toast.error(typeof err === 'string' ? err : err?.message || 'Failed to send');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="w-5 h-5" />Send Message</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Participant</Label>
            <Select value={selectedParticipant} onValueChange={v => { setSelectedParticipant(v); setSelectedContact(''); }}>
              <SelectTrigger data-testid="msg-participant"><SelectValue placeholder="Select participant" /></SelectTrigger>
              <SelectContent>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Contact</Label>
            <Select value={selectedContact} onValueChange={setSelectedContact} disabled={!contacts.length}>
              <SelectTrigger data-testid="msg-contact"><SelectValue placeholder={contacts.length ? "Select contact" : "Select participant first"} /></SelectTrigger>
              <SelectContent>{contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.firstName} {c.lastName} ({c.relationship})</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {contactDetail && (
            <div className="text-xs bg-muted p-2 rounded space-y-1" data-testid="contact-preferences">
              <p className="font-medium">{contactDetail.firstName} {contactDetail.lastName} — {contactDetail.preferredChannel}</p>
              <div className="flex gap-1 flex-wrap">
                {contactDetail.canReceiveGeneralUpdates && <Badge variant="outline" className="text-[10px]">General</Badge>}
                {contactDetail.canReceiveScheduleChanges && <Badge variant="outline" className="text-[10px]">Schedule</Badge>}
                {contactDetail.canReceiveMedicalUpdates && <Badge variant="outline" className="text-[10px]">Medical</Badge>}
                {contactDetail.canReceiveIncidentNotifications && <Badge variant="outline" className="text-[10px]">Incidents</Badge>}
                {contactDetail.canReceiveBillingUpdates && <Badge variant="outline" className="text-[10px]">Billing</Badge>}
              </div>
            </div>
          )}

          {eligibility && !eligibility.allowed && (
            <div className="flex items-center gap-2 text-amber-600 bg-amber-50 p-2 rounded text-xs" data-testid="consent-warning">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>Contact has not consented to {form.messageType.replace(/_/g, ' ').toLowerCase()} messages. Send will be blocked.</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Channel</Label>
              <Select value={form.channel} onValueChange={v => set('channel', v)}>
                <SelectTrigger data-testid="msg-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Type</Label>
              <Select value={form.messageType} onValueChange={v => set('messageType', v)}>
                <SelectTrigger data-testid="msg-type"><SelectValue /></SelectTrigger>
                <SelectContent>{MSG_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Subject</Label><Input data-testid="msg-subject" value={form.subject} onChange={e => set('subject', e.target.value)} /></div>
          <div><Label>Message</Label><Textarea data-testid="msg-body" value={form.messageBody} onChange={e => set('messageBody', e.target.value)} rows={4} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="send-message-btn" onClick={handleSend} disabled={sending || !selectedParticipant || !selectedContact || !form.subject || !form.messageBody}>
            <Send className="w-4 h-4 mr-2" />{sending ? 'Sending...' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CommunicationCentrePage() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dirFilter, setDirFilter] = useState('all');
  const [showSend, setShowSend] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (typeFilter !== 'all') params.append('messageType', typeFilter);
      if (dirFilter !== 'all') params.append('direction', dirFilter);
      const { data } = await api.get(`/engagement/messages?${params}`);
      setMessages(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load messages'); }
    finally { setLoading(false); }
  }, [page, typeFilter, dirFilter]);

  useEffect(() => { load(); }, [load]);

  const handleMarkRead = async (id) => {
    try {
      await api.post(`/engagement/messages/${id}/mark-read`);
      toast.success('Marked as read'); load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="communication-centre-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Mail className="w-6 h-6" />Communication Centre</h1>
        <Button onClick={() => setShowSend(true)} data-testid="send-message-open-btn"><Send className="w-4 h-4 mr-2" />Send Message</Button>
      </div>

      <div className="flex gap-3 flex-wrap">
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-48" data-testid="type-filter"><SelectValue placeholder="Message Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{MSG_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={dirFilter} onValueChange={v => { setDirFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="direction-filter"><SelectValue placeholder="Direction" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="OUTBOUND">Outbound</SelectItem><SelectItem value="INBOUND">Inbound</SelectItem></SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="space-y-2">
          {messages.map(m => {
            const StatusIcon = STATUS_ICONS[m.status] || Clock;
            const statusColor = STATUS_COLORS[m.status] || 'text-muted-foreground';
            return (
              <Card key={m.id} className="cursor-pointer hover:bg-accent/50 transition-colors" onClick={() => setSelected(m)}>
                <CardContent className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <StatusIcon className={`w-4 h-4 shrink-0 ${statusColor}`} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{m.messageType?.replace(/_/g, ' ')}</Badge>
                        <Badge variant="secondary" className="text-[10px]">{m.channel}</Badge>
                        <Badge variant={m.direction === 'OUTBOUND' ? 'default' : 'outline'} className="text-[10px]">{m.direction}</Badge>
                      </div>
                      <p className="font-medium truncate mt-0.5">{m.subject}</p>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {m.participantName && <span className="mr-2">{m.participantName}</span>}
                        {m.contactName && <span className="mr-2">To: {m.contactName}</span>}
                        <span>{new Date(m.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`text-[10px] ${m.status === 'READ' ? 'bg-purple-500 text-white' : m.status === 'FAILED' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'}`}>{m.status}</Badge>
                    {m.status === 'SENT' && <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); handleMarkRead(m.id); }} data-testid={`mark-read-${m.id}`}><Eye className="w-3 h-3" /></Button>}
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {messages.length === 0 && <p className="text-center text-muted-foreground py-8">No messages found</p>}
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{total} total</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={messages.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Message Detail</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Status:</span><Badge className={`${m_status_bg(selected.status)} text-white`}>{selected.status}</Badge></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Type:</span><span>{selected.messageType?.replace(/_/g, ' ')}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Channel:</span><span>{selected.channel}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Direction:</span><span>{selected.direction}</span></div>
              {selected.participantName && <div className="flex justify-between"><span className="text-muted-foreground">Participant:</span><span>{selected.participantName}</span></div>}
              {selected.contactName && <div className="flex justify-between"><span className="text-muted-foreground">Contact:</span><span>{selected.contactName}</span></div>}
              {selected.sentByName && <div className="flex justify-between"><span className="text-muted-foreground">Sent by:</span><span>{selected.sentByName}</span></div>}
              <div><span className="text-muted-foreground">Subject:</span><p className="font-medium mt-1">{selected.subject}</p></div>
              <div><span className="text-muted-foreground">Message:</span><div className="mt-1 p-3 rounded bg-muted whitespace-pre-wrap max-h-48 overflow-y-auto">{selected.messageBody}</div></div>
              <div className="text-xs text-muted-foreground">
                Created: {new Date(selected.createdAt).toLocaleString()}
                {selected.readAt && <span className="ml-3">Read: {new Date(selected.readAt).toLocaleString()}</span>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SendMessageDialog open={showSend} onClose={() => setShowSend(false)} onSent={() => { setShowSend(false); load(); }} />
    </div>
  );
}

function m_status_bg(s) {
  return s === 'READ' ? 'bg-purple-500' : s === 'FAILED' ? 'bg-red-500' : s === 'SENT' ? 'bg-green-500' : 'bg-blue-500';
}
